import { ChevronRight, Globe, Home } from "lucide-react";
import { useNavigate } from "react-router";
import { PageStatusPill } from "./PageStatusPill";
import type { PageForListing } from "../pages/types";
import { formatLongDate, relativeFromNow } from "../date-utils";

/**
 * One row in the pages list. Status pill + optional Home badge on
 * the metadata line, title as the display-serif anchor, slug in
 * mono as the subtitle. Right column shows updated date + relative.
 * Same hover/focus treatment as LandingPageRow so the two lists
 * feel like one product.
 */
export function PageRow({ page }: { page: PageForListing }) {
  const navigate = useNavigate();
  const longDate = formatLongDate(page.updatedAt);
  const relative = relativeFromNow(page.updatedAt) ?? "just now";

  return (
    <button
      type="button"
      onClick={() => navigate(`/website/pages/${page.id}`)}
      className="group relative flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition-colors duration-150 hover:bg-paper-2 active:bg-canvas/60 focus-visible:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tide/40"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-2 left-0 w-[3px] origin-top -translate-x-1 scale-y-50 rounded-r bg-tide opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:scale-y-100 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:scale-y-100 group-focus-visible:opacity-100"
      />

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <PageStatusPill status={page.status} />
          {page.isHome && (
            <span className="inline-flex items-center gap-1 rounded-full bg-tide/10 px-2 py-0.5 text-[10.5px] font-medium leading-tight text-tide">
              <Home size={10} strokeWidth={2} />
              Home
            </span>
          )}
          {page.showInNav && (
            <span className="inline-flex items-center gap-1 text-[10.5px] text-ink-4" title="Shown in navigation">
              <Globe size={10} strokeWidth={2} />
              Nav
            </span>
          )}
        </div>
        <div className="font-display truncate text-[17px] font-medium text-ink transition-colors duration-150 group-hover:text-tide group-focus-visible:text-tide">
          {page.title || "Untitled page"}
        </div>
        <div className="mt-0.5 truncate font-mono text-[12px] text-ink-4">
          /{page.slug || "—"}
        </div>
      </div>
      <div className="flex-shrink-0 text-right text-[11px] text-ink-4">
        <div className="num">{longDate}</div>
        <div className="mt-0.5">{relative}</div>
      </div>
      <ChevronRight
        size={16}
        strokeWidth={1.8}
        aria-hidden
        className="flex-shrink-0 -translate-x-1 text-ink-5 opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:text-tide group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
      />
    </button>
  );
}

